import { Calendar, Copy, Info, Navigation, Truck } from 'lucide-react';
import { useState } from 'react';
import {
  CardProductTransactionItem,
  type TransactionItemProduct,
} from '@/domains/mobile-web/components/CardProductTransaction/CardProductTransaction';
import {
  type RefundStatusType,
  STATUS_REFUND,
} from '@/domains/mobile-web/constant/listStatusRefund.constant';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';
import { useColorStore } from '@/store/colorStore';

export interface RefundOrderProduct {
  id: string;
  product: TransactionItemProduct & { id: string | number };
  quantity: number;
}

export interface RefundOrderShipping {
  courier: string;
  trackingNumber: string;
  note?: string;
}

/** 'full' = dengan footer estimasi + tombol aksi (halaman daftar); 'compact' = kartu saja. */
export type CardRefundOrderVariant = 'full' | 'compact';

export interface CardRefundOrderProps {
  date: string;
  status: RefundStatusType;
  /** Nomor invoice atau kode refund yang ditampilkan di bawah tanggal. */
  code: string;
  products: RefundOrderProduct[];
  variant?: CardRefundOrderVariant;
  /** Nominal di footer (hanya variant 'full'). */
  estimatedRefund?: number;
  /** Info kurir + resi di bawah produk (untuk status dikirim dan seterusnya). */
  shipping?: RefundOrderShipping;
  /** Tampilkan checkbox di tiap produk. Default: false. */
  selectable?: boolean;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  /** Judul & subjudul di atas daftar produk (hanya saat selectable). */
  selectTitle?: string;
  selectSubtitle?: string;
  /** Catatan biru di bawah daftar produk. Kosongkan untuk menyembunyikan. */
  note?: string;
  /** Aksi footer (variant 'full'); tombol yang muncul mengikuti status. */
  onDetail?: () => void;
  onStartReturn?: () => void;
  onTrack?: () => void;
  onCopy?: (value: string) => void;
  className?: string;
}

const BTN_BASE = 'h-10 flex-1 rounded-[8px] text-[16px]';
const BTN_PRIMARY = 'bg-text-blue-100 text-white hover:bg-text-blue-100/90';
const BTN_OUTLINE = 'border border-blue-cs-200 bg-white text-black hover:bg-[#F3F8FF]';

const formatPrice = (price: number) => new Intl.NumberFormat('id-ID').format(price);

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {
    // clipboard tidak tersedia, abaikan
  }
};

/** Catatan kecil berlatar biru muda dengan ikon info. */
export const RefundNote = ({ text, className }: { text: string; className?: string }) => (
  <div
    className={cn(
      'flex items-start gap-2 rounded-[8px] bg-[#007AFF1F] p-2 reg12 text-black',
      className
    )}
  >
    <Info size={16} strokeWidth={1.8} className="mt-0.5 shrink-0 text-text-blue-100" />
    <p>{text}</p>
  </div>
);

const CardRefundOrder = ({
  date,
  status,
  code,
  products,
  variant = 'compact',
  estimatedRefund = 0,
  shipping,
  selectable = false,
  selectedIds = [],
  onToggle,
  selectTitle = 'Pilih produk yang ingin dikembalikan.',
  selectSubtitle = 'Centang buku yang ingin kamu ajukan refund',
  note,
  onDetail,
  onStartReturn,
  onTrack,
  onCopy,
  className,
}: CardRefundOrderProps) => {
  const { NEWCOLOR } = useColorStore();
  const statusConfig = STATUS_REFUND[status];
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (value: string) => {
    copyToClipboard(value);
    onCopy?.(value);
    setCopied(value);
    window.setTimeout(() => setCopied((prev) => (prev === value ? null : prev)), 1500);
  };

  const isFull = variant === 'full';

  return (
    <article className={cn('shadowCard flex w-full flex-col gap-3 bg-white p-3', className)}>
      {/* Tanggal + status */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 body1medium text-black">
          <Calendar size={18} strokeWidth={1.8} className="text-text-grey" />
          <span>{date}</span>
        </div>
        <span
          className="rounded-[6px] px-2 py-1 reg12 whitespace-nowrap"
          style={{ backgroundColor: NEWCOLOR[statusConfig.bg], color: statusConfig.color }}
        >
          {statusConfig.label}
        </span>
      </div>

      {/* Kode + salin */}
      <div className="flex items-center justify-between gap-2 rounded-[8px] bg-[#F3F8FF] px-3 py-2">
        <span className="body1 text-text-grey truncate">{code}</span>
        <button
          type="button"
          onClick={() => handleCopy(code)}
          aria-label={`Salin ${code}`}
          className="flex shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
        >
          <Copy size={14} strokeWidth={1.8} />
          {copied === code ? 'Tersalin' : 'Salin'}
        </button>
      </div>

      {selectable && (
        <div>
          <p className="body1medium text-black">{selectTitle}</p>
          <p className="reg12 text-text-grey">{selectSubtitle}</p>
        </div>
      )}

      {/* Daftar produk */}
      <div className="flex flex-col">
        {products.map((item, index) => (
          <div key={item.id} className="flex items-start gap-2">
            {selectable && (
              <input
                type="checkbox"
                aria-label={`Pilih ${item.product.title}`}
                checked={selectedIds.includes(item.id)}
                onChange={() => onToggle?.(item.id)}
                className="mt-2! h-5! w-5! shrink-0 cursor-pointer rounded-[4px] border border-blue-cs-200 accent-text-blue-100"
              />
            )}
            <CardProductTransactionItem
              product={item.product}
              quantity={item.quantity}
              isLast={index === products.length - 1}
              className="min-w-0 flex-1"
            />
          </div>
        ))}
      </div>

      {/* Info pengiriman */}
      {shipping && (
        <div className="flex flex-col gap-1 rounded-[8px] bg-[#007AFF1F] p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 body1medium text-black">
              <Truck size={18} strokeWidth={1.8} className="text-text-blue-100" />
              <span>{shipping.courier}</span>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(shipping.trackingNumber)}
              aria-label={`Salin nomor resi ${shipping.trackingNumber}`}
              className="flex shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
            >
              <Copy size={14} strokeWidth={1.8} />
              {copied === shipping.trackingNumber ? 'Tersalin' : shipping.trackingNumber}
            </button>
          </div>
          {shipping.note && <p className="reg12 text-text-grey">{shipping.note}</p>}
        </div>
      )}

      {note && <RefundNote text={note} />}

      {/* Footer: estimasi + aksi sesuai status */}
      {isFull && (
        <>
          <div className="h-px w-full bg-blue-cs-200" />
          <div className="flex items-center gap-3">
            <div className="flex shrink-0 flex-col">
              <span className="reg12 text-text-grey">Estimasi Refund</span>
              <span className="title2bold text-black">Rp {formatPrice(estimatedRefund)}</span>
            </div>

            <div className="flex flex-1 items-center justify-end gap-2">
              {/* Disetujui: mulai kirim balik barang */}
              {status === 'approved' && (
                <Button type="button" onClick={onStartReturn} className={cn(BTN_BASE, BTN_PRIMARY)}>
                  Mulai Pengembalian
                </Button>
              )}

              {/* Dikirim: lacak paket */}
              {status === 'shipped' && (
                <Button type="button" onClick={onTrack} className={cn(BTN_BASE, BTN_OUTLINE)}>
                  <Navigation size={16} strokeWidth={2} />
                  Lacak
                </Button>
              )}

              {/* Status lain: lihat detail */}
              {status !== 'approved' && status !== 'shipped' && (
                <Button type="button" onClick={onDetail} className={cn(BTN_BASE, BTN_OUTLINE)}>
                  Details
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </article>
  );
};

export default CardRefundOrder;
